import React, { useState, useMemo } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import ChequeForm from './ChequeForm';
import { sendTelegramNotification, formatDateFR } from '../../shared/utils/helpers';
import { buildTelegramMessage } from '../../../utils/telegramMessages';
import { Plus, CheckCircle, AlertCircle, Edit2, Trash2 } from 'lucide-react';

export default function ChequesPage({ settings }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const { data: cheques, loading, remove, update } = useCollection('cheques');
  
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all | received | paid
  const [filterStatus, setFilterStatus] = useState('all'); // all | pending | cashed | bounced | cancelled
  const [search, setSearch] = useState('');

  const handleDelete = async (id) => {
    if (!confirm(isAr ? 'هل تريد حذف هذا الشيك؟' : 'Supprimer ce chèque?')) return;
    try {
      await remove(id);
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const handleQuickStatus = async (cheque, newStatus) => {
    try {
      await update(cheque.id, { status: newStatus });
      
      // Telegram notification
      const validStatuses = ['cashed', 'bounced', 'cancelled'];
      if (validStatuses.includes(newStatus)) {
        await sendTelegramNotification(
          buildTelegramMessage('cheque_status', {
            number: cheque.chequeNumber, amount: cheque.amount,
            currency: settings.currency || 'MAD', status: newStatus
          }),
          'cheque_status'
        );
      }
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    const oneDayMs = 86400000;
    
    const received = cheques.filter(c => c.type === 'received');
    const paid = cheques.filter(c => c.type === 'paid');
    const pending = cheques.filter(c => c.status === 'pending');
    const overdue = cheques.filter(c => {
      return c.status === 'pending' && c.dueDate && c.dueDate < todayStr;
    });
    const upcoming5d = cheques.filter(c => {
      if (c.status !== 'pending' || !c.dueDate) return false;
      const diff = (new Date(c.dueDate) - new Date(todayStr)) / oneDayMs;
      return diff >= 0 && diff <= 5;
    });
    
    const totalPendingReceived = received.filter(c => c.status === 'pending')
      .reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
    const totalPendingPaid = paid.filter(c => c.status === 'pending')
      .reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
    
    return {
      total: cheques.length,
      received: received.length,
      paid: paid.length,
      pending: pending.length,
      overdue: overdue.length,
      upcoming5d: upcoming5d.length,
      totalPendingReceived,
      totalPendingPaid
    };
  }, [cheques]);

  // Filter cheques
  const filtered = useMemo(() => {
    let result = cheques;
    if (filterType !== 'all') result = result.filter(c => c.type === filterType);
    if (filterStatus !== 'all') result = result.filter(c => c.status === filterStatus);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        (c.chequeNumber || '').toLowerCase().includes(q) ||
        (c.accountHolder || '').toLowerCase().includes(q) ||
        (c.bankName || '').toLowerCase().includes(q) ||
        (c.note || '').toLowerCase().includes(q)
      );
    }
    // Sort by due date (closest first for pending, most recent for others)
    return [...result].sort((a, b) => {
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      return (a.dueDate || '').localeCompare(b.dueDate || '');
    });
  }, [cheques, filterType, filterStatus, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          💵 {isAr ? `الشيكات (${cheques.length})` : `Chèques (${cheques.length})`}
        </h1>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
        >
          <Plus className="w-5 h-5" />
          {isAr ? 'شيك جديد' : 'Nouveau chèque'}
        </button>
      </div>
      
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">📥</span>
            <span className="text-xs font-bold text-green-700 uppercase">{isAr ? 'مستلم' : 'Reçus'}</span>
          </div>
          <div className="text-2xl font-black text-green-900">{stats.received}</div>
          <div className="text-xs text-green-700 font-mono mt-0.5">
            + {stats.totalPendingReceived.toFixed(0)} {settings.currency || 'MAD'}
          </div>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">📤</span>
            <span className="text-xs font-bold text-red-700 uppercase">{isAr ? 'صادر' : 'Émis'}</span>
          </div>
          <div className="text-2xl font-black text-red-900">{stats.paid}</div>
          <div className="text-xs text-red-700 font-mono mt-0.5">
            - {stats.totalPendingPaid.toFixed(0)} {settings.currency || 'MAD'}
          </div>
        </div>
        <div className={`p-3 rounded-xl border ${stats.overdue > 0 ? 'bg-red-50 border-red-300 animate-pulse' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">🚨</span>
            <span className={`text-xs font-bold uppercase ${stats.overdue > 0 ? 'text-red-700' : 'text-gray-500'}`}>
              {isAr ? 'متأخر' : 'En retard'}
            </span>
          </div>
          <div className={`text-2xl font-black ${stats.overdue > 0 ? 'text-red-900' : 'text-gray-400'}`}>{stats.overdue}</div>
        </div>
        <div className={`p-3 rounded-xl border ${stats.upcoming5d > 0 ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">⏰</span>
            <span className={`text-xs font-bold uppercase ${stats.upcoming5d > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
              {isAr ? 'قريب (5ي)' : 'Bientôt (5j)'}
            </span>
          </div>
          <div className={`text-2xl font-black ${stats.upcoming5d > 0 ? 'text-amber-900' : 'text-gray-400'}`}>{stats.upcoming5d}</div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="mb-3 flex flex-wrap gap-2 items-center">
        <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1.5 text-xs font-bold ${filterType === 'all' ? 'bg-blue-500 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
          >
            {isAr ? 'الكل' : 'Tous'}
          </button>
          <button
            onClick={() => setFilterType('received')}
            className={`px-3 py-1.5 text-xs font-bold ${filterType === 'received' ? 'bg-green-500 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
          >
            📥 {isAr ? 'مستلم' : 'Reçus'}
          </button>
          <button
            onClick={() => setFilterType('paid')}
            className={`px-3 py-1.5 text-xs font-bold ${filterType === 'paid' ? 'bg-red-500 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
          >
            📤 {isAr ? 'صادر' : 'Émis'}
          </button>
        </div>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded-lg bg-white"
        >
          <option value="all">{isAr ? 'كل الحالات' : 'Tous statuts'}</option>
          <option value="pending">⏳ {isAr ? 'في الانتظار' : 'En attente'}</option>
          <option value="cashed">✅ {isAr ? 'مصروف' : 'Encaissé'}</option>
          <option value="bounced">⚠️ {isAr ? 'مرفوض' : 'Rejeté'}</option>
          <option value="cancelled">❌ {isAr ? 'ملغى' : 'Annulé'}</option>
        </select>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={isAr ? '🔍 بحث...' : '🔍 Rechercher...'}
          className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
        />
      </div>
      
      {/* Cheques list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="text-5xl mb-3">💵</div>
          <p className="text-gray-500 font-medium">
            {cheques.length === 0
              ? (isAr ? 'لا توجد شيكات بعد' : 'Aucun chèque enregistré')
              : (isAr ? 'لا توجد نتائج' : 'Aucun résultat')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => {
            const todayStr = new Date().toISOString().split('T')[0];
            const daysLeft = c.dueDate ? Math.ceil((new Date(c.dueDate) - new Date(todayStr)) / 86400000) : null;
            const isOverdue = c.status === 'pending' && daysLeft !== null && daysLeft < 0;
            const isUrgent = c.status === 'pending' && daysLeft !== null && daysLeft >= 0 && daysLeft <= 2;
            const isSoon = c.status === 'pending' && daysLeft !== null && daysLeft > 2 && daysLeft <= 5;
            
            return (
              <div
                key={c.id}
                className={`p-3 bg-white border rounded-xl shadow-sm transition ${
                  isOverdue ? 'border-red-300 bg-red-50' :
                  isUrgent ? 'border-orange-300 bg-orange-50' :
                  isSoon ? 'border-amber-200 bg-amber-50' :
                  c.status === 'cashed' ? 'border-green-200 opacity-70' :
                  c.status === 'bounced' ? 'border-red-200' :
                  c.status === 'cancelled' ? 'border-gray-200 opacity-60' :
                  'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  {/* Left side */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xl">{c.type === 'received' ? '📥' : '📤'}</span>
                      <span className="font-bold text-gray-900">{`N° ${c.chequeNumber}`}</span>
                      <span className={`text-lg font-black font-mono ${c.type === 'received' ? 'text-green-700' : 'text-red-700'}`}>
                        {`${c.type === 'received' ? '+' : '-'} ${c.amount} ${settings.currency || 'MAD'}`}
                      </span>
                      {/* Status badge */}
                      {c.status === 'pending' && isOverdue && (
                        <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse">
                          {`🚨 ${isAr ? 'متأخر' : 'EN RETARD'} (${Math.abs(daysLeft)}j)`}
                        </span>
                      )}
                      {c.status === 'pending' && isUrgent && (
                        <span className="px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full">
                          {daysLeft === 0 ? '🔥 ' + (isAr ? 'اليوم!' : "AUJOURD'HUI!") : `🔥 ${daysLeft}j`}
                        </span>
                      )}
                      {c.status === 'pending' && isSoon && (
                        <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full">
                          {`⏰ ${daysLeft}j`}
                        </span>
                      )}
                      {c.status === 'cashed' && (
                        <span className="px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full">
                          {'✅ ' + (isAr ? 'مصروف' : 'ENCAISSÉ')}
                        </span>
                      )}
                      {c.status === 'bounced' && (
                        <span className="px-2 py-0.5 bg-red-700 text-white text-[10px] font-bold rounded-full">
                          {'⚠️ ' + (isAr ? 'مرفوض' : 'REJETÉ')}
                        </span>
                      )}
                      {c.status === 'cancelled' && (
                        <span className="px-2 py-0.5 bg-gray-500 text-white text-[10px] font-bold rounded-full">
                          {'❌ ' + (isAr ? 'ملغى' : 'ANNULÉ')}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div>
                        {'👤 '}<span className="font-medium">{c.accountHolder || '-'}</span>
                        {c.bankName && <span className="ms-2">🏦 {c.bankName}</span>}
                      </div>
                      <div className="font-mono">
                        {'📅 '}{isAr ? 'الاستحقاق' : 'Échéance'}{': '}
                        <strong className={isOverdue ? 'text-red-700' : ''}>{formatDateFR(c.dueDate)}</strong>
                      </div>
                      {c.note && <div className="italic text-gray-500">📝 {c.note}</div>}
                    </div>
                  </div>
                  
                  {/* Right side - actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {c.status === 'pending' && (
                      <button
                        onClick={() => handleQuickStatus(c, 'cashed')}
                        className="p-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition"
                        title={isAr ? 'تأكيد الصرف' : 'Marquer encaissé'}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    )}
                    {c.status === 'pending' && (
                      <button
                        onClick={() => handleQuickStatus(c, 'bounced')}
                        className="p-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition"
                        title={isAr ? 'مرفوض' : 'Marquer rejeté'}
                      >
                        <AlertCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => { setEditing(c); setShowForm(true); }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {showForm && (
        <ChequeForm
          cheque={editing}
          settings={settings}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}
